/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment, useCallback, useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {Helmet} from 'react-helmet'
import {useIntl} from 'react-intl'
import {
    normalizeSetBundleProduct,
    getUpdateBundleChildArray,
    getPriceData
} from '@salesforce/retail-react-app/app/utils/product-utils'

// Components
import {Box, Button, Stack} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    useProduct,
    useProducts,
    useCategory,
    useShopperCustomersMutation,
    useShopperBasketsMutation,
    useCustomerId,
    useShopperBasketsMutationHelper
} from '@salesforce/commerce-sdk-react'

// Hooks
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useVariant} from '@salesforce/retail-react-app/app/hooks'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
// Project Components
import ProductView from '../../components/product-view'
import InformationAccordion from '@salesforce/retail-react-app/app/pages/product-detail/partials/information-accordion'

import {HTTPNotFound, HTTPError} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'

// constant
import {
    API_ERROR_MESSAGE,
    MAX_CACHE_AGE,
    TOAST_ACTION_VIEW_WISHLIST,
    TOAST_MESSAGE_ADDED_TO_WISHLIST,
    TOAST_MESSAGE_ALREADY_IN_WISHLIST,
    STALE_WHILE_REVALIDATE
} from '@salesforce/retail-react-app/app/constants'
import {CART_LIMIT_ERROR_CODE, CART_FULL_ERROR_MESSAGE} from '../../constants'
import {rebuildPathWithParams} from '@salesforce/retail-react-app/app/utils/url'
import {useHistory, useLocation, useParams} from 'react-router-dom'
import {useToast} from '../../hooks/use-toast'
import {useWishList} from '../../hooks/use-wish-list'
import Breadcrumb from '../../components/breadcrumb/index'
import {productPageStyle, viewLinkStyle} from './index.style'
import useSitePreference from '../../hooks/use-site-preference'
import RecommendedProducts from '../../components/recommended-products'
import {PDP_RECOMMENDATION, PDP_RECENTLY_VIEW} from '../../constants'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useUsid} from '@salesforce/commerce-sdk-react'
import {triggerAddToCartEvent} from '../../utills/gtm/triggerAddToCartEvent'
import useCartLimit from '../../hooks/use-cart-limit'

const ProductDetail = () => {
    const {formatMessage} = useIntl()
    const history = useHistory()
    const location = useLocation()
    const einstein = useEinstein()
    const activeData = useActiveData()
    const toast = useToast()
    const customerId = useCustomerId()
    const navigate = useNavigation()
    const {usid} = useUsid()

    const {data} = useSitePreference(
        'pdpVariationCategories,suggestedForYou,RecentlyViewedRecommendation'
    )
    const pdpVariationCategory = data?.pdpVariationCategories
    const suggestedForYou = data?.suggestedForYou
    // const RecentlyViewedRecommendation = data?.RecentlyViewedRecommendation

    /****************************** Basket *********************************/
    const {isLoading: isBasketLoading} = useCurrentBasket()
    const {isAtLimit, errorMessage: cartLimitErrorMessage} = useCartLimit()
    const [addToCartError, setAddToCartError] = useState('')
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const updateItemsInBasketMutation = useShopperBasketsMutation('updateItemsInBasket')
    const {res} = useServerContext()
    if (res) {
        res.set('Cache-Control', 'public, s-maxage=600, stale-while-revalidate=60')
    }

    /*************************** Product Detail and Category ********************/
    const {productId, productName} = useParams()

    const urlParams = new URLSearchParams(location.search)
    const [isPickUpInStore, setIsPickUpInStore] = useState(false)
    const {
        data: product,
        isLoading: isProductLoading,
        isError: isProductError,
        error: productError
    } = useProduct(
        {
            parameters: {
                id: encodeURIComponent(urlParams.get('pid') || productId),
                perPricebook: true,
                expand: [
                    'availability',
                    'promotions',
                    'options',
                    'images',
                    'prices',
                    'variations',
                    'set_products',
                    'bundled_products'
                ],
                allImages: true,
                c_isPDPRequest: true,
                c_isPickUpInStore: isPickUpInStore,
                locale: 'default'
            }
        },
        {
            // When shoppers select a different variant (and the app fetches the new data),
            // the old data is still rendered (and not the skeletons).
            keepPreviousData: true
        }
    )

    // Note: Since category needs id from product detail, it can't be server side rendered atm
    // until we can do dependent query on server
    const {
        data: category,
        isError: isCategoryError,
        error: categoryError
    } = useCategory({
        parameters: {
            id: product?.primaryCategoryId
                ? product?.primaryCategoryId
                : product?.c_primaryCatergoryID,
            levels: 1,
            locale: 'default'
        }
    })

    /****************************** Sets and Bundles *********************************/
    const [childProductSelection, setChildProductSelection] = useState({})
    const [childProductOrderability, setChildProductOrderability] = useState({})
    const [selectedBundleQuantity, setSelectedBundleQuantity] = useState(1)
    const [isItemWishlisted, setIsItemWishlisted] = useState(false)
    const childProductRefs = React.useRef({})
    const isProductASet = product?.type?.set
    const isProductABundle = product?.type?.bundle

    const [selectedQuantity, setSelectedQuantity] = useState(1)

    const priceData = useMemo(() => {
        return getPriceData(product, {selectedQuantity})
    }, [product, selectedQuantity])

    let bundleChildVariantIds = ''
    if (isProductABundle)
        bundleChildVariantIds = Object.keys(childProductSelection)
            ?.map((key) => childProductSelection[key].variant.productId)
            .join(',')

    const {data: bundleChildrenData} = useProducts(
        {
            parameters: {
                ids: bundleChildVariantIds,
                allImages: false,
                expand: ['availability', 'variations'],
                select: '(data.(id,inventory,master))',
                locale: 'default'
            }
        },
        {
            enabled: bundleChildVariantIds?.length > 0,
            keepPreviousData: true
        }
    )

    if (isProductABundle && bundleChildrenData) {
        // Loop through the bundle children and update the inventory for variant selection
        product.bundledProducts.forEach(({product: childProduct}, index) => {
            const matchingChildProduct = bundleChildrenData?.data?.find(
                (bundleChild) => bundleChild.master.masterId === childProduct.id
            )
            if (matchingChildProduct) {
                product.bundledProducts[index].product = {
                    ...childProduct,
                    inventory: matchingChildProduct.inventory
                }
            }
        })
    }

    const comboProduct = isProductASet || isProductABundle ? normalizeSetBundleProduct(product) : {}

    /**************** Error Handling ****************/

    if (isProductError) {
        const errorStatus = productError?.response?.status
        switch (errorStatus) {
            case 404:
                throw new HTTPNotFound('Product Not Found.')
            default:
                throw new HTTPError(errorStatus, `HTTP Error ${errorStatus} occurred.`)
        }
    }
    if (isCategoryError) {
        const errorStatus = categoryError?.response?.status
        switch (errorStatus) {
            case 404:
                throw new HTTPNotFound('Category Not Found.')
            default:
                throw new HTTPError(errorStatus, `HTTP Error ${errorStatus} occurred.`)
        }
    }

    const [primaryCategory, setPrimaryCategory] = useState(category)
    const variant = useVariant(product)
    // This page uses the `primaryCategoryId` to retrieve the category data. This attribute
    // is only available on `master` products. Since a variation will be loaded once all the
    // attributes are selected (to get the correct inventory values), the category information
    // is overridden. This will allow us to keep the initial category around until a different
    // master product is loaded.
    useEffect(() => {
        if (category) {
            setPrimaryCategory(category)
        }
    }, [category])

    /**************** Product Variant ****************/

    useEffect(() => {
        let selectDefault = !location?.search
        let url = location.pathname
        let defaultParams = {}
        const queryParams = Object.fromEntries(new URLSearchParams(location.search).entries())
        defaultParams = queryParams
        if (!selectDefault) {
            const params = new URLSearchParams(location?.search)
            product?.variationAttributes?.forEach((attr) => {
                const paramValue = params.get(attr?.id)

                // If value is missing or invalid, trigger default selection
                const isValid =
                    paramValue && attr?.values?.some((value) => value?.value === paramValue)

                if (!isValid) {
                    selectDefault = true
                } else if (attr?.id) {
                    // Add valid value to defaultParams
                    defaultParams[attr.id] = paramValue
                }
            })
        }

        if (selectDefault) {
            product?.variationAttributes?.forEach((attr) => {
                const orderableValues = attr?.values?.filter((value) => value?.orderable)
                if (orderableValues?.length === 1 && attr?.id && !defaultParams[attr.id]) {
                    defaultParams[attr.id] = orderableValues[0].value
                }
            })
        }
        if (variant?.productId && variant.productId.includes(productId)) {
            defaultParams.pid = variant?.productId
        }
        const updatedUrl = rebuildPathWithParams(`${url}`, defaultParams)
        history.replace(updatedUrl)
    }, [product?.master?.masterId, variant, location.pathname])

    useEffect(() => {
        const queryParams = Object.fromEntries(new URLSearchParams(location.search).entries())
        if (
            product &&
            productId === product.id &&
            product.slugUrl &&
            !(
                decodeURIComponent(product.slugUrl).includes(productName) ||
                decodeURIComponent(product.slugUrl).includes(decodeURIComponent(productName))
            ) //adding fallback logic
        ) {
            const url = new URL(product.slugUrl, window.location.origin)
            const relativePath = url.pathname.replace('/s/Cavenders', '')
            const updatedUrl = rebuildPathWithParams(relativePath, queryParams)
            history.replace(updatedUrl)
        }
    }, [product?.master?.masterId])

    // Set cart limit error message when cart is at 24 items
    useEffect(() => {
        if (isAtLimit) {
            setAddToCartError(cartLimitErrorMessage)
        } else if (!addToCartError || addToCartError === cartLimitErrorMessage) {
            // Clear error only if it's the cart limit error
            setAddToCartError('')
        }
    }, [isAtLimit, cartLimitErrorMessage])

    /**************** Wishlist ****************/
    const {data: wishlist, isLoading: isWishlistLoading} = useWishList()
    const createCustomerProductListItem = useShopperCustomersMutation(
        'createCustomerProductListItem'
    )

    const handleAddToWishlist = (product, variant, quantity) => {
        const isItemInWishlist = wishlist?.customerProductListItems?.find(
            (i) => i.productId === variant?.productId || i.productId === product?.id
        )

        if (!isItemInWishlist) {
            createCustomerProductListItem.mutate(
                {
                    parameters: {
                        listId: wishlist.id,
                        customerId
                    },
                    body: {
                        // NOTE: API does not respect quantity, it always adds 1
                        quantity,
                        productId: variant?.productId || product?.id,
                        public: false,
                        priority: 1,
                        type: 'product'
                    }
                },
                {
                    onSuccess: () => {
                        toast({
                            title: formatMessage(TOAST_MESSAGE_ADDED_TO_WISHLIST, {quantity: 1}),
                            status: 'success',
                            action: (
                                // it would be better if we could use <Button as={Link}>
                                // but unfortunately the Link component is not compatible
                                // with Chakra Toast, since the ToastManager is rendered via portal
                                // and the toast doesn't have access to intl provider, which is a
                                // requirement of the Link component.
                                <Button
                                    sx={viewLinkStyle}
                                    variant="link"
                                    onClick={() => navigate('/account/wishlist')}
                                    // onClick={() => (window.location.href = '/wishlistpage/')}
                                >
                                    {formatMessage(TOAST_ACTION_VIEW_WISHLIST)}
                                </Button>
                            )
                        })

                        // start analytics: Add to Wishlist
                        const setAnalyticsDataForAddToWishlist = () => {
                            window.dataLayer.push({
                                event: 'addToWishlistFromPDP',
                                category: 'Product',
                                action: 'Add to Wishlist',
                                label: 'PDP',
                                value: variant?.productId, // SKU/Variant ID
                                product: {
                                    id: product?.id,
                                    name: product?.name,
                                    price: `${priceData?.currentPrice}`,
                                    brand: product?.brand,
                                    category: primaryCategory?.parentCategoryTree
                                        ?.map((cat) => cat?.name)
                                        ?.join('/'),
                                    variant: variant?.productId
                                }
                            })
                        }
                        setAnalyticsDataForAddToWishlist()
                        // end analytics: Add to Wishlist
                    },
                    onError: () => {
                        showError()
                    }
                }
            )
        } else {
            toast({
                title: formatMessage(TOAST_MESSAGE_ALREADY_IN_WISHLIST),
                status: 'info',
                action: (
                    <Button
                        sx={viewLinkStyle}
                        variant="link"
                        onClick={() => navigate('/account/wishlist')}
                        // onClick={() => (window.location.href = '/wishlistpage/')}
                    >
                        {formatMessage(TOAST_ACTION_VIEW_WISHLIST)}
                    </Button>
                )
            })
        }
    }

    /**************** Add To Cart ****************/
    const showToast = useToast()
    const showError = () => {
        showToast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error'
        })
    }

    const handleAddToCart = async (productSelectionValues) => {
        try {
            const productItems = productSelectionValues?.map(
                ({variant, quantity, c_storeId, c_storepickup}) => ({
                    productId: variant.productId,
                    price: variant.price,
                    quantity,
                    c_storeId,
                    c_storepickup
                })
            )

            await addItemToNewOrExistingBasket(productItems)

            einstein.sendAddToCart(productItems)

            // start analytics: Add to Cart
            productSelectionValues.forEach(({product, quantity, c_storeId, c_storepickup}) => {
                triggerAddToCartEvent({
                    product,
                    qyt: quantity,
                    isInStorePickUp: c_storepickup || c_storeId ? true : false,
                    category,
                    usid
                })
            })
            // end analytics: Add to Cart

            // If the items were successfully added, set the return value to be used
            // by the add to cart modal.
            return productSelectionValues
        } catch (error) {
            let errorBody
            if (error?.response instanceof Response) {
                try {
                    errorBody = await error.response.clone().json()
                } catch {
                    const text = await error.response.text()
                    try {
                        errorBody = JSON.parse(text)
                    } catch {
                        errorBody = {message: text}
                    }
                }
            }

            if (errorBody?.statusCode === CART_LIMIT_ERROR_CODE) {
                // Cart is full - 24 product limit reached
                setAddToCartError(CART_FULL_ERROR_MESSAGE)
            } else if (errorBody?.statusCode === 'BUS-1001') {
                // Wishlist related error
                toast({
                    title: errorBody?.statusMessage,
                    status: 'info',
                    action: (
                        <Button
                            sx={viewLinkStyle}
                            variant="link"
                            onClick={() => navigate('/account/wishlist')}
                        >
                            {''}
                        </Button>
                    )
                })
            } else if (errorBody?.statusCode === 'SYS-1008') {
                toast({
                    title: errorBody?.statusMessage,
                    status: 'error'
                })
            } else {
                showError(error)
            }
        }
    }

    /**************** Product Set/Bundles Handlers ****************/
    const handleChildProductValidation = useCallback(() => {
        // Run validation for all child products. This will ensure the error
        // messages are shown.
        Object.values(childProductRefs.current).forEach(({validateOrderability}) => {
            validateOrderability({scrollErrorIntoView: false})
        })

        // Using ot state for which child products are selected, scroll to the first
        // one that isn't selected.
        const selectedProductIds = Object.keys(childProductSelection)
        const firstUnselectedProduct = comboProduct?.childProducts?.find(
            ({product: childProduct}) => !selectedProductIds.includes(childProduct.id)
        )?.product

        if (firstUnselectedProduct) {
            // Get the reference to the product view and scroll to it.
            const {ref} = childProductRefs.current[firstUnselectedProduct.id]

            if (ref.scrollIntoView) {
                ref.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end'
                })
            }

            return false
        }

        return true
    }, [product, childProductSelection])

    /**************** Product Set Handlers ****************/
    const handleProductSetAddToCart = () => {
        // Get all the selected products, and pass them to the addToCart handler which
        // accepts an array.
        const productSelectionValues = Object.values(childProductSelection)
        return handleAddToCart(productSelectionValues)
    }

    /**************** Product Bundle Handlers ****************/
    // Top level bundle does not have variants
    const handleProductBundleAddToCart = async (variant, selectedQuantity) => {
        try {
            const childProductSelections = Object.values(childProductSelection)

            const productItems = [
                {
                    productId: product.id,
                    price: product.price,
                    quantity: selectedQuantity,
                    // The add item endpoint in the shopper baskets API does not respect variant selections
                    // for bundle children, so we have to make a follow up call to update the basket
                    // with the chosen variant selections
                    bundledProductItems: childProductSelections.map((child) => {
                        return {
                            productId: child.variant.productId,
                            quantity: child.quantity
                        }
                    })
                }
            ]

            const res = await addItemToNewOrExistingBasket(productItems)

            const bundleChildMasterIds = childProductSelections?.map((child) => {
                return child.product.id
            })

            // since the returned data includes all products in basket
            // here we compare list of productIds in bundleProductItems of each productItem to filter out the
            // current bundle that was last added into cart
            const currentBundle = res?.productItems?.find((productItem) => {
                if (!productItem.bundledProductItems?.length) return
                const bundleChildIds = productItem.bundledProductItems?.map((item) => {
                    // seek out the bundle child that still uses masterId as product id
                    return item.productId
                })
                return bundleChildIds.every((id) => bundleChildMasterIds.includes(id))
            })

            const itemsToBeUpdated = getUpdateBundleChildArray(
                currentBundle,
                childProductSelections
            )

            if (itemsToBeUpdated.length) {
                // make a follow up call to update child variant selection for product bundle
                // since add item endpoint doesn't currently consider product bundle child variants
                await updateItemsInBasketMutation.mutateAsync({
                    method: 'PATCH',
                    parameters: {
                        basketId: res.basketId
                    },
                    body: itemsToBeUpdated
                })
            }

            einstein.sendAddToCart(productItems)

            return childProductSelections
        } catch (error) {
            showError(error)
        }
    }

    /**************** Einstein ****************/
    useEffect(() => {
        if (product && product?.type?.set) {
            einstein.sendViewProduct(product)
            const childrenProducts = product.setProducts
            childrenProducts?.map((child) => {
                try {
                    einstein.sendViewProduct(child)
                } catch (err) {
                    logger.error('Einstein sendViewProduct error', {
                        namespace: 'ProductDetail.useEffect',
                        additionalProperties: {error: err, child}
                    })
                }
                activeData.sendViewProduct(category, child, 'detail')
            })
        } else if (product) {
            try {
                einstein.sendViewProduct(product)
            } catch (err) {
                logger.error('Einstein sendViewProduct error', {
                    namespace: 'ProductDetail.useEffect',
                    additionalProperties: {error: err, product}
                })
            }
            activeData.sendViewProduct(category, product, 'detail')
        }
    }, [product])

    useEffect(() => {
        if (wishlist && product && variant) {
            const isItemInWishlist = wishlist?.customerProductListItems?.find(
                (i) => i.productId === variant?.productId
            )
            setIsItemWishlisted(isItemInWishlist ? true : false)
        }
        return () => setIsItemWishlisted(false)
    }, [wishlist, variant, product])

    const updateDeliceryType = (deliveryType) => {
        if (deliveryType === 'pickUpInStore') {
            setIsPickUpInStore(true)
        } else {
            setIsPickUpInStore(false)
        }
    }

    return (
        <Box
            className="sf-product-detail-page"
            layerStyle="page"
            data-testid="product-details-page"
            sx={productPageStyle}
        >
            <Helmet>
                <title>{product?.pageTitle ?? product?.name}</title>
                <meta name="description" content={product?.pageDescription} />
            </Helmet>

            {primaryCategory?.parentCategoryTree && (
                <Breadcrumb categories={primaryCategory?.parentCategoryTree || []} />
            )}

            <Stack spacing={16}>
                {isProductASet || isProductABundle ? (
                    <Fragment>
                        <ProductView
                            addToCartError={addToCartError}
                            isCartAtLimit={isAtLimit}
                            product={product}
                            setSelectedQuantity={setSelectedQuantity}
                            category={primaryCategory?.parentCategoryTree || []}
                            addToCart={
                                isProductASet
                                    ? handleProductSetAddToCart
                                    : handleProductBundleAddToCart
                            }
                            addToWishlist={handleAddToWishlist}
                            isProductLoading={isProductLoading}
                            isBasketLoading={isBasketLoading}
                            isWishlistLoading={isWishlistLoading}
                            validateOrderability={handleChildProductValidation}
                            childProductOrderability={childProductOrderability}
                            setSelectedBundleQuantity={setSelectedBundleQuantity}
                            isItemWishlisted={isItemWishlisted}
                            pdpVariationCategory={pdpVariationCategory}
                            updateDeliceryType={updateDeliceryType}
                            setIsPickUpInStore={setIsPickUpInStore}
                        />

                        <hr />

                        {/* TODO: consider `childProduct.belongsToSet` */}
                        {
                            // Render the child products
                            comboProduct?.childProducts?.map(
                                ({product: childProduct, quantity: childQuantity}) => (
                                    <Box key={childProduct.id} data-testid="child-product">
                                        <ProductView
                                            // Do not use an arrow function as we are manipulating the functions scope.
                                            ref={function (ref) {
                                                // Assign the "set" scope of the ref, this is how we access the internal
                                                // validation.
                                                childProductRefs.current[childProduct.id] = {
                                                    ref,
                                                    validateOrderability: this.validateOrderability
                                                }
                                            }}
                                            addToCartError={addToCartError}
                                            setSelectedQuantity={setSelectedQuantity}
                                            product={childProduct}
                                            isProductPartOfSet={isProductASet}
                                            isProductPartOfBundle={isProductABundle}
                                            childOfBundleQuantity={childQuantity}
                                            selectedBundleParentQuantity={selectedBundleQuantity}
                                            addToCart={
                                                isProductASet
                                                    ? (
                                                          variant,
                                                          quantity,
                                                          c_storeId,
                                                          c_storepickup
                                                      ) =>
                                                          handleAddToCart([
                                                              {
                                                                  product: childProduct,
                                                                  variant,
                                                                  quantity,
                                                                  c_storeId,
                                                                  c_storepickup
                                                              }
                                                          ])
                                                    : null
                                            }
                                            addToWishlist={
                                                isProductASet ? handleAddToWishlist : null
                                            }
                                            onVariantSelected={(product, variant, quantity) => {
                                                if (quantity) {
                                                    setChildProductSelection((previousState) => ({
                                                        ...previousState,
                                                        [product.id]: {
                                                            product,
                                                            variant,
                                                            quantity: isProductABundle
                                                                ? childQuantity
                                                                : quantity
                                                        }
                                                    }))
                                                } else {
                                                    const selections = {...childProductSelection}
                                                    delete selections[product.id]
                                                    setChildProductSelection(selections)
                                                }
                                            }}
                                            isProductLoading={isProductLoading}
                                            isBasketLoading={isBasketLoading}
                                            isWishlistLoading={isWishlistLoading}
                                            setChildProductOrderability={
                                                setChildProductOrderability
                                            }
                                            isItemWishlisted={isItemWishlisted}
                                            pdpVariationCategory={pdpVariationCategory}
                                            updateDeliceryType={updateDeliceryType}
                                            setIsPickUpInStore={setIsPickUpInStore}
                                        />
                                        <InformationAccordion product={childProduct} />

                                        <Box display={['none', 'none', 'none', 'block']}>
                                            <hr />
                                        </Box>
                                    </Box>
                                )
                            )
                        }
                    </Fragment>
                ) : (
                    <Fragment>
                        <ProductView
                            addToCartError={addToCartError}
                            isCartAtLimit={isAtLimit}
                            product={product}
                            setSelectedQuantity={setSelectedQuantity}
                            category={primaryCategory?.parentCategoryTree || []}
                            addToCart={(variant, quantity, c_storeId, c_storepickup) =>
                                handleAddToCart([
                                    {product, variant, quantity, c_storeId, c_storepickup}
                                ])
                            }
                            addToWishlist={handleAddToWishlist}
                            isProductLoading={isProductLoading}
                            isBasketLoading={isBasketLoading}
                            isWishlistLoading={isWishlistLoading}
                            isItemWishlisted={isItemWishlisted}
                            pdpVariationCategory={pdpVariationCategory}
                            updateDeliceryType={updateDeliceryType}
                            setIsPickUpInStore={setIsPickUpInStore}
                        />
                    </Fragment>
                )}

                {/* Product Recommendations */}
                <Stack>
                    <RecommendedProducts
                        title={suggestedForYou}
                        recommender={PDP_RECOMMENDATION}
                        fetchKey={product?.id}
                        products={[product]}
                        showQuickView={false}
                    />
                    {/* <RecommendedProducts
                        title={RecentlyViewedRecommendation}
                        recommender={PDP_RECENTLY_VIEW}
                        fetchKey={product?.id}
                        products={[product]}
                    /> */}
                </Stack>
            </Stack>
        </Box>
    )
}

ProductDetail.getTemplateName = () => 'product-detail'

ProductDetail.propTypes = {
    /**
     * The current react router match object. (Provided internally)
     */
    match: PropTypes.object
}

export default ProductDetail
